// ─────────────────────────────────────────────────────────────────────────────
// MODULE_NAME — central registry of module identifiers across the app.
//
// Each entry is a constant identifier (the TS key, e.g. `INPUT_MODULE`)
// bound to its kebab-case identifier (the platform module id,
// e.g. `'input-module'`). The kebab-case string is what flows into API
// params + filter state; the constant identifier is what shows up in
// component code where a stable reference is needed.
// ─────────────────────────────────────────────────────────────────────────────

export const MODULE_NAME = {
  // ----- Nav-tree entries (from the `modules` permission table) -----
  DASHBOARD: 'dashboard',
  FACILITY: 'facility',
  COLDROOM: 'coldroom',
  SENSOR: 'sensor',
  DEVICE: 'device-management',
  V_TAG: 'v-tag',
  ACCESS_POINT: 'access-point',
  ALARMS: 'alarms',
  REPORTS: 'reports',
  DOCUMENTS: 'documents',
  KPI: 'kpi',
  BRANCH_AND_USER: 'branch-and-user',
  USER: 'user',
  // The branch permission payload returns the User Management sub-page under
  // slug `user-management` (distinct from the bare `user` key it also
  // emits). The sidebar's "User Management" entry is gated by this slug —
  // the bare `user` key still exists in MODULE_NAME for parity with the
  // permission payload but is not used in the registry.
  USER_MANAGEMENT: 'user-management',
  ROLE: 'role',
  BRANCH: 'branch',
  HELP: 'help',
  WHATS_NEW: 'whats-new',
  NOTIFICATION: 'notification',
  ASSET_TAG: 'asset-tag',
  GROUP: 'group',
  REMINDER: 'reminder',
  REPORT_CONFIGURATION: 'report-configuration',
  SUPPORT_TICKET: 'support-ticket',
  AUDIT_TRSILS: 'audit-trails',
  GLOBAL_SETTINGS: 'global-settings',
  EXTERNAL_MODULE: 'external-module',
  INPUT_MODULE: 'input-module',
} as const

// `ModuleKey` — TS key of MODULE_NAME (e.g. `'INPUT_MODULE'`). `ModuleId`
// — the kebab-case platform id (e.g. `'input-module'`).
export type ModuleKey = keyof typeof MODULE_NAME
export type ModuleId = (typeof MODULE_NAME)[ModuleKey]

// Derives dropdown options from MODULE_NAME. Each option pairs:
//   • value — the constant identifier (e.g. `'INPUT_MODULE'`)
//   • name  — the kebab-case string (`'input-module'`)
// Consumers pick the constant for selection plumbing and the kebab id
// for any downstream filtering / API call.
export const getModuleOptions = () =>
  (Object.keys(MODULE_NAME) as ModuleKey[]).map((key) => ({
    value: key,
    name: MODULE_NAME[key],
  }))

// Inverse lookup — resolves a kebab-case module id back to its constant
// identifier (e.g. `'input-module'` → `'INPUT_MODULE'`). Returns the
// kebab id unchanged when no match is found so the helper is safe to
// pipe through untrusted filter values.
export const getModuleKey = (moduleId) => {
  if (!moduleId) return ''
  const entry = (Object.entries(MODULE_NAME) as [ModuleKey, ModuleId][]).find(
    ([, id]) => id === moduleId
  )
  return entry ? entry[0] : moduleId
}
